import { Controller } from "@hotwired/stimulus"
import { coastNumber, fireNumber, monthsToCoast, monthsToTarget, project, realReturn } from "../javascript/fire_math"
import { compact, duration, money, monthYear, percent } from "../javascript/fire_view"

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

export default class extends Controller {
  static targets = ["form", "progress", "goal", "bar", "eta", "etaDetail", "retireLabel", "retireValue", "retireIncome", "canvas", "monthly", "monthlyTile", "field"]
  static values = {
    mode: String,
    value: Number,
    monthly: Number,
    history: Array,
    startedOn: String,
    startValue: Number,
    startMonthly: Number,
    currency: String,
    symbol: String
  }

  connect() {
    this.update()
    document.fonts.ready.then(() => this.fieldTargets.forEach((field) => this.fit(field)))
  }

  disconnect() {
    this.chart?.destroy()
  }

  changeMonthly() {
    const field = this.formTarget.elements["fire_plan[monthly_contribution]"]
    field.scrollIntoView({ behavior: "smooth", block: "center" })
    field.focus({ preventScroll: true })
  }

  fit(field) {
    const style = getComputedStyle(field)
    this.measure ??= document.createElement("canvas").getContext("2d")
    this.measure.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
    field.style.width = `${Math.ceil(this.measure.measureText(field.value || field.placeholder || "0").width) + 2}px`
  }

  read(name) {
    const value = parseFloat(this.formTarget.elements[`fire_plan[${name}]`]?.value)
    return Number.isFinite(value) ? value : null
  }

  get money() {
    return { code: this.currencyValue, symbol: this.symbolValue }
  }

  inputs() {
    const age = this.read("age")
    const retireAge = this.read("retire_age")
    return {
      spending: Math.max(0, this.read("spending") || 0),
      withdrawal: (this.read("withdrawal") || 0) / 100,
      annual: realReturn({ nominal: (this.read("return") || 0) / 100, inflation: (this.read("inflation") || 0) / 100 }),
      sideIncome: Math.max(0, this.read("side_income") || 0),
      monthly: this.read("monthly_contribution") > 0 ? this.read("monthly_contribution") : this.monthlyValue,
      age,
      retireAge,
      retirement: age !== null && retireAge !== null && retireAge > age ? (retireAge - age) * 12 : null
    }
  }

  outlook(inputs) {
    const plan = { portfolio: this.valueValue, monthly: inputs.monthly, annual: inputs.annual }
    const fire = fireNumber(inputs.spending, inputs.withdrawal)

    if (this.modeValue === "coast") {
      if (inputs.retirement === null) return { target: null, months: null }
      return { target: coastNumber(fire, inputs.annual, inputs.retirement), fire, months: monthsToCoast({ ...plan, target: fire, months: inputs.retirement }) }
    }

    const target = this.modeValue === "barista" ? fireNumber(Math.max(0, inputs.spending - inputs.sideIncome), inputs.withdrawal) : fire
    return { target, fire, months: target > 0 ? monthsToTarget({ ...plan, target }) : null }
  }

  update() {
    const inputs = this.inputs()
    const outlook = this.outlook(inputs)

    if (this.hasProgressTarget && outlook.target) {
      const share = Math.min(1, this.valueValue / outlook.target)
      this.progressTarget.textContent = percent(this.valueValue / outlook.target)
      this.goalTarget.textContent = `${money(this.valueValue, this.money)} of ${money(outlook.target, this.money)}`
      this.barTarget.style.width = `${share * 100}%`
      this.etaTarget.textContent = outlook.months === null ? "Out of reach" : monthYear(outlook.months, true)
      this.etaDetailTarget.textContent = this.etaDetail(outlook.months, inputs.age)
    }

    if (this.hasMonthlyTarget) this.monthlyTarget.textContent = money(inputs.monthly, this.money)
    this.fieldTargets.forEach((field) => this.fit(field))
    if (this.hasMonthlyTileTarget) this.monthlyTileTarget.classList.toggle("fire-planner__tile--tracked", !(this.read("monthly_contribution") > 0))
    if (this.hasRetireValueTarget) this.renderRetirement(inputs)

    this.drawChart(inputs, outlook)
  }

  renderRetirement(inputs) {
    if (inputs.retirement === null) {
      this.retireLabelTarget.textContent = "At retirement"
      this.retireValueTarget.textContent = "—"
      this.retireIncomeTarget.textContent = "Add your age and retirement age"
      return
    }

    const coast = this.modeValue === "coast"
    const points = project({ portfolio: this.valueValue, monthly: coast ? 0 : inputs.monthly, annual: inputs.annual, months: inputs.retirement })
    const total = points[points.length - 1].value

    this.retireLabelTarget.textContent = `At ${inputs.retireAge}${coast ? " without new money" : ""}`
    this.retireValueTarget.textContent = money(total, this.money)
    this.retireIncomeTarget.textContent = `${money(total * inputs.withdrawal, this.money)} a year at ${percent(inputs.withdrawal)}`
  }

  etaDetail(months, age) {
    if (months === null) return "Save more or expect a higher return"
    if (months === 0) return "You're already there"
    return [age !== null && `Age ${Math.floor(age + months / 12)}`, `in ${duration(months)}`].filter(Boolean).join(" · ")
  }

  monthIndex(date) {
    return date.getFullYear() * 12 + date.getMonth()
  }

  label(index) {
    return `${MONTHS[index % 12]} ${Math.floor(index / 12)}`
  }

  drawChart(inputs, outlook) {
    const today = this.monthIndex(new Date())
    const started = this.monthIndex(new Date(this.startedOnValue))
    const history = new Map(this.historyValue.map(([date, value]) => [this.monthIndex(new Date(date)), value]))
    const first = Math.min(started, today, ...history.keys())
    const last = today + Math.min(600, (outlook.months ?? 360) + 12)
    const indexes = Array.from({ length: last - first + 1 }, (_, offset) => first + offset)

    const planned = project({ portfolio: this.startValueValue, monthly: this.startMonthlyValue, annual: inputs.annual, months: last - started })
    const projected = project({ portfolio: this.valueValue, monthly: inputs.monthly, annual: inputs.annual, months: last - today })
    const goal = outlook.fire ?? outlook.target

    const datasets = [
      this.line("Actual", indexes.map((index) => (index === today ? this.valueValue : history.get(index) ?? null)), "#00d6b4", { backgroundColor: "rgba(0, 214, 180, 0.08)", fill: "origin", spanGaps: true }),
      this.line("Plan", indexes.map((index) => (index >= started ? planned[index - started].value : null)), "#ba54f5", { borderDash: [6, 4] }),
      this.line("Projection", indexes.map((index) => (index >= today ? projected[index - today].value : null)), "rgba(255, 255, 255, 0.5)", { borderDash: [2, 4] }),
      goal && this.line(this.modeValue === "barista" ? "Barista FIRE number" : "FIRE number", indexes.map(() => goal), "#e14eca", { borderWidth: 1 })
    ].filter(Boolean)

    const labels = indexes.map((index) => this.label(index))

    if (this.chart) {
      this.chart.data.labels = labels
      this.chart.data.datasets = datasets
      this.chart.update()
      return
    }

    this.chart = new window.Chart(this.canvasTarget.getContext("2d"), {
      type: "line",
      data: { labels, datasets },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        animation: false,
        legend: { display: false },
        hover: { mode: "index", intersect: false },
        tooltips: {
          mode: "index",
          intersect: false,
          backgroundColor: "#fff",
          titleFontColor: "#333",
          bodyFontColor: "#666",
          bodySpacing: 4,
          xPadding: 12,
          filter: (item) => item.yLabel !== null && !Number.isNaN(item.yLabel),
          callbacks: { label: (item, data) => `${data.datasets[item.datasetIndex].label}: ${money(item.yLabel, this.money)}` }
        },
        scales: {
          yAxes: [{
            gridLines: { drawBorder: false, color: "rgba(255, 255, 255, 0.06)", zeroLineColor: "rgba(255, 255, 255, 0.06)" },
            ticks: { beginAtZero: true, maxTicksLimit: 5, padding: 8, fontColor: "#9a9a9a", callback: (value) => compact(value, this.money) }
          }],
          xAxes: [{
            gridLines: { display: false, drawBorder: false },
            ticks: { maxTicksLimit: 8, maxRotation: 0, padding: 8, fontColor: "#9a9a9a" }
          }]
        }
      }
    })
  }

  line(label, values, color, options = {}) {
    return { label, data: values.map((value) => (value === null ? null : Math.round(value))), borderColor: color, borderWidth: 2, fill: false, pointRadius: 0, pointHoverRadius: 0, lineTension: 0, ...options }
  }
}
